import {
  Tag,
  TagFormData,
  TagQueryParams,
  ApiResponse
} from '../types/space';
import { apiClient } from '../config/netConfig';

// API基础路径 - 指向tags端点
const BASE_PATH = 'tags';

// 清理SVG内容用于API传输
const cleanSvgForApi = (svgContent: string): string => {
  if (!svgContent || !svgContent.trim()) {
    return '';
  }

  try {
    // 移除BOM和多余的空白字符
    svgContent = svgContent.replace(/^\uFEFF/, '').trim();

    // 验证是否是有效的SVG
    if (!svgContent.includes('<svg') || !svgContent.includes('</svg>')) {
      throw new Error('无效的SVG格式');
    }

    // 移除可能的危险属性和脚本
    svgContent = svgContent
      .replace(/on\w+="[^"]*"/gi, '') // 移除事件处理器
      .replace(/javascript:/gi, '') // 移除javascript协议
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // 移除script标签
      .replace(/style="[^"]*javascript[^"]*"/gi, ''); // 移除包含javascript的style

    // 压缩SVG：移除多余的空白字符和换行
    svgContent = svgContent
      .replace(/>\s+</g, '><') // 移除标签间的空白
      .replace(/\s+/g, ' ') // 压缩多个空格为一个
      .trim();

    return svgContent;
  } catch (error) {
    console.error('SVG清理失败:', error);
    return '';
  }
};

class TagService {
  // 获取标签列表
  async getTags(params?: TagQueryParams): Promise<ApiResponse<Tag[]>> {
    try {


      // 构建查询参数
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.pageSize) queryParams.append('limit', params.pageSize.toString());
      if (params?.keyword || params?.search) {
        queryParams.append('search', (params.keyword || params.search || ''));
      }

      const url = queryParams.toString() ? `${BASE_PATH}?${queryParams}` : BASE_PATH;


      const response = await apiClient.get(url);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || [],
          pagination: response.data.pagination
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '获取标签列表失败'
        };
      }
    } catch (error: any) {
      console.error('获取标签列表失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取标签列表失败'
      };
    }
  }

  // 获取单个标签
  async getTag(id: string): Promise<ApiResponse<Tag>> {
    try {

      console.log('标签ID:', id);

      const response = await apiClient.get(`${BASE_PATH}/${id}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          data: null,
          error: response.data.message || '获取标签详情失败'
        };
      }
    } catch (error: any) {
      console.error('获取标签详情失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || '获取标签详情失败'
      };
    }
  }

  // 创建标签
  async createTag(data: TagFormData): Promise<ApiResponse<Tag>> {
    try {


      // 清理SVG内容
      const cleanData = {
        ...data,
        icon: cleanSvgForApi(data.icon || '')
      };

      const response = await apiClient.post(BASE_PATH, cleanData);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          data: null,
          error: response.data.message || '创建标签失败'
        };
      }
    } catch (error: any) {
      console.error('创建标签失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || '创建标签失败'
      };
    }
  }

  // 更新标签
  async updateTag(id: string, data: Partial<TagFormData>): Promise<ApiResponse<Tag>> {
    try {

      console.log('标签ID:', id);
      console.log('更新数据:', data);

      // 清理SVG内容
      const cleanData = {
        ...data,
        icon: data.icon ? cleanSvgForApi(data.icon) : undefined
      };

      const response = await apiClient.put(`${BASE_PATH}/${id}`, cleanData);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          data: null,
          error: response.data.message || '更新标签失败'
        };
      }
    } catch (error: any) {
      console.error('更新标签失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || '更新标签失败'
      };
    }
  }

  // 删除标签
  async deleteTag(id: string): Promise<ApiResponse<boolean>> {
    try {

      console.log('标签ID:', id);

      const response = await apiClient.delete(`${BASE_PATH}/${id}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: true
        };
      } else {
        return {
          success: false,
          data: false,
          error: response.data.message || '删除标签失败'
        };
      }
    } catch (error: any) {
      console.error('删除标签失败:', error);
      return {
        success: false,
        data: false,
        error: error.response?.data?.message || error.message || '删除标签失败'
      };
    }
  }

  // 搜索标签
  async searchTags(keyword: string): Promise<ApiResponse<Tag[]>> {
    try {

      console.log('搜索关键词:', keyword);

      const response = await apiClient.get(`${BASE_PATH}/search?keyword=${encodeURIComponent(keyword)}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || []
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '搜索标签失败'
        };
      }
    } catch (error: any) {
      console.error('搜索标签失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '搜索标签失败'
      };
    }
  }

  // 为空间添加标签
  async addTagToSpace(spaceId: string, tagId: string): Promise<ApiResponse<boolean>> {
    try {

      console.log('空间ID:', spaceId);
      console.log('标签ID:', tagId);

      const response = await apiClient.post(`${BASE_PATH}/spaces/${spaceId}/tags`, { tagId });
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: true
        };
      } else {
        return {
          success: false,
          data: false,
          error: response.data.message || '添加空间标签失败'
        };
      }
    } catch (error: any) {
      console.error('添加空间标签失败:', error);
      return {
        success: false,
        data: false,
        error: error.response?.data?.message || error.message || '添加空间标签失败'
      };
    }
  }

  // 从空间移除标签
  async removeTagFromSpace(spaceId: string, tagId: string): Promise<ApiResponse<boolean>> {
    try {

      console.log('空间ID:', spaceId);
      console.log('标签ID:', tagId);

      const response = await apiClient.delete(`${BASE_PATH}/spaces/${spaceId}/tags/${tagId}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: true
        };
      } else {
        return {
          success: false,
          data: false,
          error: response.data.message || '移除空间标签失败'
        };
      }
    } catch (error: any) {
      console.error('移除空间标签失败:', error);
      return {
        success: false,
        data: false,
        error: error.response?.data?.message || error.message || '移除空间标签失败'
      };
    }
  }

  // 获取标签统计数据
  async getTagStatistics(): Promise<ApiResponse<{
    total: number;
    withIcon: number;
    withColor: number;
    withDescription: number;
  }>> {
    try {
      const response = await apiClient.get(`/${BASE_PATH}/statistics`);
      return {
        success: true,
        data: response.data.data  // 修复：获取嵌套的data字段
      };
    } catch (error: any) {
      console.error('获取标签统计数据失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || '获取标签统计数据失败'
      };
    }
  }
}

// 创建服务实例
const tagService = new TagService();

// 导出实例
export default tagService;